import { DownOutlined, PlusOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import type { CSSProperties, ReactNode, RefObject } from "react";
import { Checkbox, Col, Form, Input, Radio, Row, Switch } from "antd";
import type { FormInstance } from "antd";
import CustomCard from "../../components/CustomCard";
import CustomButton from "../../components/CustomButton";
import { ProductSelectInput } from "../../components/ProductSelectInput";
import { AmountInput } from "../../components/form/AmountInput";
import { SelectInput } from "../../components/form/SelectInput";
import styles from "../OrderForms/FinancialForm.module.css";
import type { OrderFinancialVO } from "../OrderForms/orders";
import {
  getZuuFnaCanonicalQuestionKey,
  getZuuFnaQuestionAliases,
  normalizeZuuFnaQuestionList,
  toZuuFnaStoredQuestionValue,
} from "./zuuFnaFieldMap";

const checkboxGroupStyle: CSSProperties = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

function formatDisplayValue(value: unknown) {
  if (value === undefined || value === null || value === "") {
    return "-";
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
  }
  const numeric = Number(value);
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(numeric)) {
    return numeric.toLocaleString("en-US", { maximumFractionDigits: 0 });
  }
  return String(value);
}

function DetailValue({
  content,
  suffix,
  className,
}: {
  content: unknown;
  suffix?: string;
  className?: string;
}) {
  const displayValue = formatDisplayValue(content);
  return (
    <div className={[styles.zuuReadonlyValue, className].filter(Boolean).join(" ")}>
      {displayValue === "-" ? displayValue : suffix ? `${displayValue} ${suffix}` : displayValue}
    </div>
  );
}

function DetailSplitRow({
  label,
  value,
  valueClassName,
}: {
  label: ReactNode;
  value: ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className={styles.zuuDetailSplitRow}>
      <div className={styles.zuuDetailSplitLabel}>{label}</div>
      <div className={styles.zuuDetailSplitValue}>
        <div className={valueClassName}>{value}</div>
      </div>
    </div>
  );
}

export type FnaQuestionSection =
  | "financialAnalysis"
  | "transferInsuranceDeclaration"
  | "suggestionsAndReasons";

export type ZuuGetQuestionValue = (
  section: FnaQuestionSection,
  questionKey: string,
  type?: "RADIO" | "CHECKBOX" | "INPUT",
) => any;

export type ZuuUpdateQuestionValue = (
  section: FnaQuestionSection,
  questionKey: string,
  value: any,
  type?: "RADIO" | "CHECKBOX" | "INPUT",
) => void;

export type ZuuFinancialAnalysisSectionProps = {
  data: OrderFinancialVO;
  onChange: (data: OrderFinancialVO) => void;
  getQuestionValue: ZuuGetQuestionValue;
  updateQuestionValue: ZuuUpdateQuestionValue;
  shouldShowQuestion42: () => boolean;
  metrics: {
    aMonthly: number;
    aYearly: number;
    a1Monthly: number;
    a1Yearly: number;
    bAssets: number;
    b1Amount: number;
  };
};

export type ZuuPremiumFinancingSectionProps = {
  data: OrderFinancialVO;
  onChange: (data: OrderFinancialVO) => void;
  readOnly?: boolean;
  companyOptions: Array<{ label: string; value: string }>;
  onFieldChange: (field: keyof OrderFinancialVO | string, value: any) => void;
  onAddPremiumFinancing: () => void;
  onRemovePremiumFinancing: (index: number) => void;
  onPremiumFinancingChange: (
    index: number,
    field: string,
    value: any,
  ) => void;
};

export type ZuuProductSelectionSectionProps = {
  data: OrderFinancialVO;
  onChange: (data: OrderFinancialVO) => void;
  readOnly?: boolean;
  productSectionRef?: RefObject<HTMLDivElement | null>;
  productValidation: Record<
    number,
    { paymentTermValid: boolean; benefitTermValid: boolean }
  >;
  getQuestionValue: ZuuGetQuestionValue;
  onOpenSelector?: (
    context: { type: string; index: number },
    productType?: string,
  ) => void;
  onMessageError: (content: string) => void;
};

export type ZuuSuggestionsSectionProps = {
  data: OrderFinancialVO;
  onChange: (data: OrderFinancialVO) => void;
  getQuestionValue: ZuuGetQuestionValue;
  updateQuestionValue: ZuuUpdateQuestionValue;
};

export type ZuuOnlyProvideOneProductSectionProps = {
  data: OrderFinancialVO;
  onChange: (data: OrderFinancialVO) => void;
};

type ZuuFnaFormProps = {
  form: FormInstance;
  readOnly?: boolean;
  detailDisplay?: boolean;
  className?: string;
  children?: ReactNode;
  zuuFinancialAnalysisProps?: ZuuFinancialAnalysisSectionProps;
  zuuPremiumFinancingProps?: ZuuPremiumFinancingSectionProps;
  zuuProductSelectionProps?: ZuuProductSelectionSectionProps;
  zuuOnlyProvideOneProductProps?: ZuuOnlyProvideOneProductSectionProps;
  zuuSuggestionsProps?: ZuuSuggestionsSectionProps;
};

export function ZuuFnaForm({
  form,
  readOnly = false,
  detailDisplay = false,
  className,
  children,
  zuuFinancialAnalysisProps,
  zuuPremiumFinancingProps,
  zuuProductSelectionProps,
  zuuOnlyProvideOneProductProps,
  zuuSuggestionsProps,
}: ZuuFnaFormProps) {
  return (
    <Form
      form={form}
      layout="vertical"
      disabled={readOnly && !detailDisplay}
      className={className}
    >
      {zuuFinancialAnalysisProps ? (
        <ZuuFinancialAnalysisSection
          form={form}
          detailDisplay={detailDisplay}
          {...zuuFinancialAnalysisProps}
        />
      ) : null}
      {zuuPremiumFinancingProps ? (
        <ZuuPremiumFinancingSection
          detailDisplay={detailDisplay}
          {...zuuPremiumFinancingProps}
        />
      ) : null}
      {zuuProductSelectionProps ? (
        <ZuuProductSelectionSection
          detailDisplay={detailDisplay}
          {...zuuProductSelectionProps}
        />
      ) : null}
      {zuuOnlyProvideOneProductProps ? (
        <ZuuOnlyProvideOneProductSection
          detailDisplay={detailDisplay}
          {...zuuOnlyProvideOneProductProps}
        />
      ) : null}
      {zuuSuggestionsProps ? (
        <ZuuSuggestionsSection
          form={form}
          detailDisplay={detailDisplay}
          {...zuuSuggestionsProps}
        />
      ) : null}
      {children}
    </Form>
  );
}

function ZuuFinancialAnalysisSection({
  form,
  data,
  onChange,
  getQuestionValue,
  updateQuestionValue,
  detailDisplay = false,
}: ZuuFinancialAnalysisSectionProps & {
  form: FormInstance;
  detailDisplay?: boolean;
}) {
  const selectedObjectives =
    getQuestionValue(
      "financialAnalysis",
      "insurancePurchaseObjectives",
      "CHECKBOX",
    ) || [];
  const investmentManagementPreference = getQuestionValue(
    "financialAnalysis",
    "investmentOptionManagementPreference",
    "RADIO",
  );
  const premiumPaymentPeriodRange = getQuestionValue(
    "financialAnalysis",
    "willingPremiumPaymentPeriodRange",
    "RADIO",
  );
  const additionalLifeCoverageAmountOption = getQuestionValue(
    "financialAnalysis",
    "additionalLifeCoverageAmountOption",
    "RADIO",
  );
  const additionalCriticalIllnessCoverageAmountOption = getQuestionValue(
    "financialAnalysis",
    "additionalCriticalIllnessCoverageAmountOption",
    "RADIO",
  );
  const selectedHasLongTermGoal =
    selectedObjectives.includes("C") ||
    selectedObjectives.includes("D") ||
    selectedObjectives.includes("E");
  const additionalTargetAmountOption = getQuestionValue(
    "financialAnalysis",
    "additionalTargetAmountOption",
    "RADIO",
  );
  const otherObjectiveOptionValue =
    form.getFieldValue("insurancePurchaseOtherObjectiveOptions") ??
    getQuestionValue(
      "financialAnalysis",
      "insurancePurchaseOtherObjectiveOptions",
      "RADIO",
    );
  const normalizeCurrencyOption = (value: string | undefined) => {
    if (value === "A" || value === "HKD") return "A";
    if (value === "B" || value === "USD") return "B";
    if (value === "C" || value === "N/A") return "C";
    return undefined;
  };
  const normalizedAdditionalLifeCoverageAmountOption = normalizeCurrencyOption(
    additionalLifeCoverageAmountOption,
  );
  const normalizedAdditionalCriticalIllnessCoverageAmountOption =
    normalizeCurrencyOption(additionalCriticalIllnessCoverageAmountOption);
  const normalizedAdditionalTargetAmountOption = normalizeCurrencyOption(
    additionalTargetAmountOption,
  );
  const premiumToDisposableIncomeRatio = getQuestionValue(
    "financialAnalysis",
    "premiumToDisposableIncomeRatio",
    "RADIO",
  );
  const premiumToLiquidAssetsRatio = getQuestionValue(
    "financialAnalysis",
    "premiumToLiquidAssetsRatio",
    "RADIO",
  );
  const policyTargetExpectedTime = getQuestionValue(
    "financialAnalysis",
    "policyTargetExpectedTime",
    "RADIO",
  );
  const premiumRatioOptionLabels: Record<string, string> = {
    A: "≤10%",
    B: "11%-20%",
    C: "21%-40%",
    D: "41%-60%",
    E: "61%-80%",
    F: "81%-100%",
  };
  const paymentPeriodOptionLabels: Record<string, string> = {
    A: "2-5 年",
    B: "6-10 年",
    C: "11-15 年",
    D: "16-20 年",
    E: "超過20年",
    F: "終身",
  };
  const policyTargetExpectedTimeLabels: Record<string, string> = {
    A: "A. < 1 年",
    B: "B. 1-5年",
    C: "C. 6-10年",
    D: "D. 11-15年",
    E: "E. 16-20年",
    F: "F. > 20 年",
    G: "G. 終身",
  };
  const medicalObjectiveLabels: Record<string, string> = {
    A: "(i) 產品迎合為我住院時提供入息保障的保險目標，而此產品為醫療保險產品並提供住院入息保障",
    B: "(ii) 產品迎合為我提供保障以應付日益增長的醫療費用的目標，而此產品為醫療保險產品並提供住院實報實銷保障",
    C: "(iii) 產品迎合為我提供健康保障／危疾保障以應付將來的健康服務的目標，此產品提供健康保障／危疾保障一筆過賠償",
  };
  const investmentManagementPreferenceLabels: Record<string, string> = {
    A: "(i) 本人願意按個人決定（毋須獲授權保險人及／或持牌保險中介人提供任何專業意見的情況）選擇及管理保險產品項下的不同投資選項／投資選擇（如有），並且願意在保險產品的目標利益／保障期的整個期間作出此決定。",
    B: "(ii) 本人願意按個人決定（經獲授權保險人及／或持牌保險中介人提供專業意見的情況）選擇及管理保險產品項下的不同投資選項／投資選擇（如有），並且願意在保險產品的目標利益／保障期的整個期間作出此決定。",
    C: "(iii) 本人不願意選擇或管理保險產品項目下的不同投資選項／投資選擇（如有）。",
  };
  const otherObjectiveOptionLabels: Record<string, string> = {
    A: "a. 付款人保障",
    B: "b. 付款人保障連危疾",
    C: "c. 免付保費",
    D: "d. 遺產規劃",
    E: "e. 其他",
  };
  const findQuestionIndex = (questions: any[], questionKey: string) => {
    const aliases = new Set([
      ...getZuuFnaQuestionAliases(questionKey),
      getZuuFnaCanonicalQuestionKey(questionKey),
    ]);
    return questions.findIndex((question) => aliases.has(question.question));
  };

  const applyQuestionValueToList = (
    questions: any[],
    questionKey: string,
    value: any,
    type: "RADIO" | "CHECKBOX" | "INPUT" = "RADIO",
  ) => {
    const nextQuestions = [...questions];
    const { canonicalKey, storedType, storedValue } = toZuuFnaStoredQuestionValue(
      questionKey,
      value,
      type,
    );
    const aliases = new Set(getZuuFnaQuestionAliases(questionKey));
    const questionIndex = nextQuestions.findIndex((question) =>
      aliases.has(question.question),
    );

    let newItems: any[] = [];
    if (questionIndex >= 0) {
      newItems = [...(nextQuestions[questionIndex].items || [])];
    }

    if (storedType === "INPUT") {
      const itemIndex = newItems.findIndex((item) => item.key === "A");
      if (itemIndex >= 0) {
        newItems[itemIndex] = { ...newItems[itemIndex], value: storedValue };
      } else {
        newItems.push({ key: "A", value: storedValue });
      }
    } else if (storedType === "RADIO") {
      const key = storedValue;
      let keyFound = false;
      newItems = newItems.map((item) => {
        if (item.key === key) {
          keyFound = true;
          return { ...item, value: true };
        }
        return { ...item, value: false };
      });

      if (!keyFound && key) {
        newItems.push({ key, value: true });
      }
    } else {
      const keys = (storedValue as string[]) || [];
      newItems = newItems.map((item) => ({
        ...item,
        value: keys.includes(item.key),
      }));
      keys.forEach((key) => {
        if (!newItems.find((item) => item.key === key)) {
          newItems.push({ key, value: true });
        }
      });
    }

    if (questionIndex >= 0) {
      nextQuestions[questionIndex] = {
        ...nextQuestions[questionIndex],
        question: canonicalKey,
        type: storedType,
        items: newItems,
      };
    } else {
      nextQuestions.push({
        question: canonicalKey,
        type: storedType,
        items: newItems,
      });
    }

    return nextQuestions;
  };

  return (
    <CustomCard
      title="財務需要分析"
      className={`${styles.sectionCard} ${styles.zuuSectionCard}`}
      bodyStyle={{ paddingTop: 0 }}
    >
      <div className={styles.zuuSectionContent}>
        <Form.Item
          label="1. 閣下購買保險產品的目標為何？ (勾選一項或多項)"
          name="insurancePurchaseObjectives"
          rules={[{ required: true, message: "請至少選擇一項" }]}
        >
          <Checkbox.Group
            style={checkboxGroupStyle}
            value={selectedObjectives}
            onChange={(checkedValues) => {
              const currentFinancialAnalysis = normalizeZuuFnaQuestionList(
                data.financialAnalysis || [],
              );
              const objectivesIndex = currentFinancialAnalysis.findIndex(
                (question) => question.question === "insurancePurchaseObjectives",
              );
              const keys = (checkedValues as string[]) || [];
              const objectivesItems = keys.map((key) => ({
                key,
                value: true,
              }));

              if (objectivesIndex >= 0) {
                currentFinancialAnalysis[objectivesIndex] = {
                  ...currentFinancialAnalysis[objectivesIndex],
                  items: objectivesItems,
                };
              } else {
                currentFinancialAnalysis.push({
                  question: "insurancePurchaseObjectives",
                  type: "CHECKBOX",
                  items: objectivesItems,
                });
              }

              const currentValues =
                getQuestionValue(
                  "financialAnalysis",
                  "insurancePurchaseObjectives",
                  "CHECKBOX",
                ) || [];

              const newlyChecked = keys.filter(
                (value) => !currentValues.includes(value),
              );

              newlyChecked.forEach((option) => {
                if (option === "A") {
                  const questionKeys = [
                    "additionalLifeCoverageAmountOption",
                    "additionalLifeCoverageAmountHKD",
                    "additionalLifeCoverageAmountUSD",
                  ];

                  questionKeys.forEach((questionKey) => {
                    const existingIndex = findQuestionIndex(currentFinancialAnalysis, questionKey);
                    if (existingIndex >= 0) {
                      return;
                    }

                    currentFinancialAnalysis.push({
                      question: getZuuFnaCanonicalQuestionKey(questionKey),
                      type:
                        questionKey === "additionalLifeCoverageAmountOption"
                          ? "RADIO"
                          : "INPUT",
                      items:
                        questionKey === "additionalLifeCoverageAmountOption"
                          ? []
                          : [],
                    });
                  });
                  return;
                } else if (option === "B") {
                  const questionKeys = [
                    "insurancePurchaseMedicalObjectives",
                    "additionalCriticalIllnessCoverageAmountOption",
                    "additionalCriticalIllnessCoverageAmountHKD",
                    "additionalCriticalIllnessCoverageAmountUSD",
                  ];

                  questionKeys.forEach((questionKey) => {
                    const existingIndex = findQuestionIndex(currentFinancialAnalysis, questionKey);
                    if (existingIndex >= 0) {
                      return;
                    }

                    currentFinancialAnalysis.push({
                      question: getZuuFnaCanonicalQuestionKey(questionKey),
                      type:
                        questionKey === "insurancePurchaseMedicalObjectives"
                          ? "RADIO"
                          :
                        questionKey ===
                        "additionalCriticalIllnessCoverageAmountOption"
                          ? "RADIO"
                          : "INPUT",
                      items:
                        questionKey === "insurancePurchaseMedicalObjectives"
                          ? []
                          :
                        questionKey ===
                        "additionalCriticalIllnessCoverageAmountOption"
                          ? []
                          : [],
                    });
                  });
                  return;
                } else if (option === "C" || option === "D") {
                  const questionKeys = [
                    "additionalTargetAmountOption",
                    "additionalTargetAmountHKD",
                    "additionalTargetAmountUSD",
                    "additionalTargetYears",
                  ];
                  questionKeys.forEach((questionKey) => {
                    const existingIndex = findQuestionIndex(currentFinancialAnalysis, questionKey);
                    if (existingIndex >= 0) return;
                    currentFinancialAnalysis.push({
                      question: getZuuFnaCanonicalQuestionKey(questionKey),
                      type:
                        questionKey === "additionalTargetAmountOption"
                          ? "RADIO"
                          : "INPUT",
                      items:
                        questionKey === "additionalTargetAmountOption"
                          ? []
                          : [],
                    });
                  });
                  return;
                } else if (option === "E") {
                  const questionKeys = [
                    "additionalTargetAmountOption",
                    "additionalTargetAmountHKD",
                    "additionalTargetAmountUSD",
                    "additionalTargetYears",
                  ];
                  questionKeys.forEach((questionKey) => {
                    const existingIndex = findQuestionIndex(currentFinancialAnalysis, questionKey);
                    if (existingIndex >= 0) return;
                    currentFinancialAnalysis.push({
                      question: getZuuFnaCanonicalQuestionKey(questionKey),
                      type:
                        questionKey === "additionalTargetAmountOption"
                          ? "RADIO"
                          : "INPUT",
                      items:
                        questionKey === "additionalTargetAmountOption"
                          ? []
                          : [],
                    });
                  });
                  return;
                } else if (option === "F") {
                  const questionKeys = [
                    "insurancePurchaseOtherObjectiveOptions",
                    "insurancePurchaseOtherObjectives",
                  ];
                  questionKeys.forEach((questionKey) => {
                    const existingIndex = findQuestionIndex(currentFinancialAnalysis, questionKey);
                    if (existingIndex >= 0) return;
                    currentFinancialAnalysis.push({
                      question: getZuuFnaCanonicalQuestionKey(questionKey),
                      type:
                        questionKey === "insurancePurchaseOtherObjectiveOptions"
                          ? "RADIO"
                          : "INPUT",
                      items:
                        questionKey === "insurancePurchaseOtherObjectiveOptions"
                          ? []
                          : [],
                    });
                  });
                  return;
                }
              });

              const newlyUnchecked = currentValues.filter(
                (value: string) => !keys.includes(value),
              );

              newlyUnchecked.forEach((option: string) => {
                const fieldsToClear: string[] = [];

                if (option === "A") {
                  fieldsToClear.push(
                    "additionalLifeCoverageAmount",
                    "additionalLifeCoverageAmountOption",
                    "additionalLifeCoverageAmountHKD",
                    "additionalLifeCoverageAmountUSD",
                  );
                } else if (option === "B") {
                  fieldsToClear.push(
                    "additionalCriticalIllnessCoverageAmount",
                    "insurancePurchaseMedicalObjectives",
                    "additionalCriticalIllnessCoverageAmountOption",
                    "additionalCriticalIllnessCoverageAmountHKD",
                    "additionalCriticalIllnessCoverageAmountUSD",
                  );
                } else if (option === "C" || option === "D") {
                  const otherOption = option === "C" ? "D" : "C";
                  if (
                    !keys.includes(otherOption) &&
                    !keys.includes("E")
                  ) {
                    fieldsToClear.push(
                      "additionalTargetAmountOption",
                      "additionalTargetAmountHKD",
                      "additionalTargetAmountUSD",
                      "additionalTargetYears",
                    );
                  }
                } else if (option === "E") {
                  fieldsToClear.push("investmentOptionManagementPreference");
                  if (!keys.includes("C") && !keys.includes("D")) {
                    fieldsToClear.push(
                      "additionalTargetAmountOption",
                      "additionalTargetAmountHKD",
                      "additionalTargetAmountUSD",
                      "additionalTargetYears",
                    );
                  }
                } else if (option === "F") {
                  fieldsToClear.push(
                    "insurancePurchaseOtherObjectiveOptions",
                    "insurancePurchaseOtherObjectives",
                  );
                }

                fieldsToClear.forEach((fieldKey) => {
                  const fieldIndex = findQuestionIndex(currentFinancialAnalysis, fieldKey);
                  if (fieldIndex >= 0) {
                    currentFinancialAnalysis[fieldIndex] = {
                      ...currentFinancialAnalysis[fieldIndex],
                      question: getZuuFnaCanonicalQuestionKey(fieldKey),
                      items: [],
                    };
                  }
                  form.setFieldValue(fieldKey, undefined);
                });
              });

              onChange({
                ...data,
                financialAnalysis: currentFinancialAnalysis,
              });

              form.setFieldValue("insurancePurchaseObjectives", checkedValues);
            }}
          >
            <div className={styles.zuuObjectiveOption}>
              <Checkbox value="A" className={styles.zuuObjectiveOptionLabel}>
                A. 為應付不時之需提供財務保障（如死亡、意外、殘疾）
              </Checkbox>
              {selectedObjectives.includes("A") ? (
                <div className={styles.zuuInsetPanel}>
                  <Form.Item
                    label="受保人需要的額外人壽保障額為？（請選一項）"
                    name="additionalLifeCoverageAmountOption"
                    rules={[
                      {
                        required: true,
                        message: "請選擇一項",
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    {detailDisplay ? (
                      <DetailSplitRow
                        label="受保人需要的額外人壽保障額為？（請選一項）"
                        value={
                          normalizedAdditionalLifeCoverageAmountOption === "A" ? (
                            <DetailValue
                              content={getQuestionValue(
                                "financialAnalysis",
                                "additionalLifeCoverageAmountHKD",
                                "INPUT",
                              )}
                              suffix="HKD"
                              className={styles.zuuReadonlyInputLike}
                            />
                          ) : normalizedAdditionalLifeCoverageAmountOption === "B" ? (
                            <DetailValue
                              content={getQuestionValue(
                                "financialAnalysis",
                                "additionalLifeCoverageAmountUSD",
                                "INPUT",
                              )}
                              suffix="USD"
                              className={styles.zuuReadonlyInputLike}
                            />
                          ) : (
                            <DetailValue
                              content="不適用"
                              className={styles.zuuReadonlyInputLike}
                            />
                          )
                        }
                      />
                    ) : (
                    <Radio.Group
                      className={styles.zuuCurrencyOptionGrid}
                      value={normalizedAdditionalLifeCoverageAmountOption}
                      onClickCapture={(event) => {
                        const target = event.target as HTMLElement;
                        logLifeCoverageEvent("click-capture", {
                          targetTag: target.tagName,
                          targetText: target.textContent?.trim() || "",
                          targetValue:
                            target instanceof HTMLInputElement ? target.value : undefined,
                        });
                      }}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        let nextFinancialAnalysis = normalizeZuuFnaQuestionList(
                          data.financialAnalysis || [],
                        );
                        logLifeCoverageEvent("group-change-before", {
                          nextValue,
                        });
                        nextFinancialAnalysis = applyQuestionValueToList(
                          nextFinancialAnalysis,
                          "additionalLifeCoverageAmountOption",
                          nextValue,
                          "RADIO",
                        );
                        form.setFieldValue(
                          "additionalLifeCoverageAmountOption",
                          nextValue,
                        );

                        if (nextValue !== "A") {
                          form.setFieldValue(
                            "additionalLifeCoverageAmountHKD",
                            undefined,
                          );
                          nextFinancialAnalysis = applyQuestionValueToList(
                            nextFinancialAnalysis,
                            "additionalLifeCoverageAmountHKD",
                            undefined,
                            "INPUT",
                          );
                        }

                        if (nextValue !== "B") {
                          form.setFieldValue(
                            "additionalLifeCoverageAmountUSD",
                            undefined,
                          );
                          nextFinancialAnalysis = applyQuestionValueToList(
                            nextFinancialAnalysis,
                            "additionalLifeCoverageAmountUSD",
                            undefined,
                            "INPUT",
                          );
                        }

                        onChange({
                          ...data,
                          financialAnalysis: nextFinancialAnalysis,
                        });

                        requestAnimationFrame(() => {
                          logLifeCoverageEvent("group-change-after-raf", {
                            nextValue,
                            formValueAfterRaf: form.getFieldValue(
                              "additionalLifeCoverageAmountOption",
                            ),
                            financialAnalysisAfterChange: nextFinancialAnalysis,
                          });
                        });
                      }}
                    >
                      <div className={styles.zuuInlineRadioField}>
                        <Radio
                          value="A"
                          aria-label="HKD"
                        />
                        <Form.Item
                          noStyle
                          name="additionalLifeCoverageAmountHKD"
                          rules={[
                            {
                              validator: (_, value) => {
                                if (
                                  normalizedAdditionalLifeCoverageAmountOption !==
                                  "A"
                                ) {
                                  return Promise.resolve();
                                }
                                if (
                                  value === undefined ||
                                  value === null ||
                                  value === 0
                                ) {
                                  return Promise.reject(
                                    new Error("請輸入 HKD 金額"),
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <AmountInput
                            style={{ width: "100%" }}
                            placeholder="請輸入"
                            suffix="HKD"
                            suffixVariant="box"
                            value={
                              getQuestionValue(
                                "financialAnalysis",
                                "additionalLifeCoverageAmountHKD",
                                "INPUT",
                              ) ?? undefined
                            }
                            onChange={(value) => {
                              updateQuestionValue(
                                "financialAnalysis",
                                "additionalLifeCoverageAmountHKD",
                                value ?? undefined,
                                "INPUT",
                              );
                              form.setFieldValue(
                                "additionalLifeCoverageAmountHKD",
                                value ?? undefined,
                              );
                            }}
                          />
                        </Form.Item>
                      </div>
                      <div className={styles.zuuInlineRadioField}>
                        <Radio
                          value="B"
                          aria-label="USD"
                        />
                        <Form.Item
                          noStyle
                          name="additionalLifeCoverageAmountUSD"
                          rules={[
                            {
                              validator: (_, value) => {
                                if (
                                  normalizedAdditionalLifeCoverageAmountOption !==
                                  "B"
                                ) {
                                  return Promise.resolve();
                                }
                                if (
                                  value === undefined ||
                                  value === null ||
                                  value === 0
                                ) {
                                  return Promise.reject(
                                    new Error("請輸入 USD 金額"),
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <AmountInput
                            style={{ width: "100%" }}
                            placeholder="請輸入"
                            suffix="USD"
                            suffixVariant="box"
                            value={
                              getQuestionValue(
                                "financialAnalysis",
                                "additionalLifeCoverageAmountUSD",
                                "INPUT",
                              ) ?? undefined
                            }
                            onChange={(value) => {
                              updateQuestionValue(
                                "financialAnalysis",
                                "additionalLifeCoverageAmountUSD",
                                value ?? undefined,
                                "INPUT",
                              );
                              form.setFieldValue(
                                "additionalLifeCoverageAmountUSD",
                                value ?? undefined,
                              );
                            }}
                          />
                        </Form.Item>
                      </div>
                      <Radio
                        value="C"
                        className={styles.zuuGridFullSpan}
                      >
                        不適用
                      </Radio>
                    </Radio.Group>
                    )}
                  </Form.Item>
                </div>
              ) : null}
            </div>

            <div className={styles.zuuObjectiveOption}>
              <Checkbox value="B" className={styles.zuuObjectiveOptionLabel}>
                B. 為醫療需要作準備（例如：危疾，住院等）
              </Checkbox>
              {selectedObjectives.includes("B") ? (
                <>
                  <div className={styles.zuuInsetPanel}>
                    <Form.Item
                      name="insurancePurchaseMedicalObjectives"
                      rules={[{ required: true, message: "請選擇一項" }]}
                      style={{ marginBottom: 0 }}
                    >
                      {detailDisplay ? (
                        <DetailSplitRow
                          label="為醫療需要作準備（例如：危疾，住院等）"
                          value={
                            <DetailValue
                              content={
                                medicalObjectiveLabels[
                                  getQuestionValue(
                                    "financialAnalysis",
                                    "insurancePurchaseMedicalObjectives",
                                    "RADIO",
                                  ) || ""
                                ] || "-"
                              }
                              className={styles.zuuReadonlyInputLike}
                            />
                          }
                        />
                      ) : (
                        <Radio.Group
                          className={styles.zuuOptionColumn}
                          value={
                            getQuestionValue(
                              "financialAnalysis",
                              "insurancePurchaseMedicalObjectives",
                              "RADIO",
                            )
                          }
                          onChange={(event) => {
                            updateQuestionValue(
                              "financialAnalysis",
                              "insurancePurchaseMedicalObjectives",
                              event.target.value,
                              "RADIO",
                            );
                            form.setFieldValue(
                              "insurancePurchaseMedicalObjectives",
                              event.target.value,
                            );
                          }}
                        >
                          <Radio value="A">
                            (i) 產品迎合為我住院時提供入息保障的保險目標，而此產品為醫療保險產品並提供住院入息保障
                          </Radio>
                          <Radio value="B">
                            (ii) 產品迎合為我提供保障以應付日益增長的醫療費用的目標，而此產品為醫療保險產品並提供住院實報實銷保障
                          </Radio>
                          <Radio value="C">
                            (iii) 產品迎合為我提供健康保障／危疾保障以應付將來的健康服務的目標，此產品提供健康保障／危疾保障一筆過賠償
                          </Radio>
                        </Radio.Group>
                      )}
                    </Form.Item>
                  </div>

                  <div className={styles.zuuInsetPanel}>
                    <Form.Item
                      label="受保人需要的額外危疾保障額為？（請選一項）"
                      name="additionalCriticalIllnessCoverageAmountOption"
                      rules={[
                        () => ({
                          validator: async (_, value) => {
                            if (!selectedObjectives.includes("B")) {
                              return;
                            }
                            if (!value) {
                              throw new Error("請選擇一項");
                            }
                          },
                        }),
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      {detailDisplay ? (
                        <DetailSplitRow
                          label="受保人需要的額外危疾保障額為？（請選一項）"
                          value={
                            normalizedAdditionalCriticalIllnessCoverageAmountOption ===
                            "A" ? (
                              <DetailValue
                                content={getQuestionValue(
                                  "financialAnalysis",
                                  "additionalCriticalIllnessCoverageAmountHKD",
                                  "INPUT",
                                )}
                                suffix="HKD"
                                className={styles.zuuReadonlyInputLike}
                              />
                            ) : normalizedAdditionalCriticalIllnessCoverageAmountOption ===
                              "B" ? (
                              <DetailValue
                                content={getQuestionValue(
                                  "financialAnalysis",
                                  "additionalCriticalIllnessCoverageAmountUSD",
                                  "INPUT",
                                )}
                                suffix="USD"
                                className={styles.zuuReadonlyInputLike}
                              />
                            ) : (
                              <DetailValue
                                content="不適用"
                                className={styles.zuuReadonlyInputLike}
                              />
                            )
                          }
                        />
                      ) : (
                      <Radio.Group
                        className={styles.zuuCurrencyOptionGrid}
                        value={
                          normalizedAdditionalCriticalIllnessCoverageAmountOption
                        }
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          let nextFinancialAnalysis = normalizeZuuFnaQuestionList(
                            data.financialAnalysis || [],
                          );
                          nextFinancialAnalysis = applyQuestionValueToList(
                            nextFinancialAnalysis,
                            "additionalCriticalIllnessCoverageAmountOption",
                            nextValue,
                            "RADIO",
                          );
                          form.setFieldValue(
                            "additionalCriticalIllnessCoverageAmountOption",
                            nextValue,
                          );

                          if (nextValue !== "A") {
                            form.setFieldValue(
                              "additionalCriticalIllnessCoverageAmountHKD",
                              undefined,
                            );
                            nextFinancialAnalysis = applyQuestionValueToList(
                              nextFinancialAnalysis,
                              "additionalCriticalIllnessCoverageAmountHKD",
                              undefined,
                              "INPUT",
                            );
                          }

                          if (nextValue !== "B") {
                            form.setFieldValue(
                              "additionalCriticalIllnessCoverageAmountUSD",
                              undefined,
                            );
                            nextFinancialAnalysis = applyQuestionValueToList(
                              nextFinancialAnalysis,
                              "additionalCriticalIllnessCoverageAmountUSD",
                              undefined,
                              "INPUT",
                            );
                          }

                          onChange({
                            ...data,
                            financialAnalysis: nextFinancialAnalysis,
                          });
                        }}
                      >
                        <div className={styles.zuuInlineRadioField}>
                          <Radio
                            value="A"
                            aria-label="HKD"
                          />
                          <Form.Item
                            noStyle
                            name="additionalCriticalIllnessCoverageAmountHKD"
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (
                                    normalizedAdditionalCriticalIllnessCoverageAmountOption !==
                                    "A"
                                  ) {
                                    return Promise.resolve();
                                  }
                                  if (
                                    value === undefined ||
                                    value === null ||
                                    value === 0
                                  ) {
                                    return Promise.reject(
                                      new Error("請輸入 HKD 金額"),
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <AmountInput
                              style={{ width: "100%" }}
                              placeholder="請輸入"
                              suffix="HKD"
                              suffixVariant="box"
                              value={
                                getQuestionValue(
                                  "financialAnalysis",
                                  "additionalCriticalIllnessCoverageAmountHKD",
                                  "INPUT",
                                ) ?? undefined
                              }
                              onChange={(value) => {
                                updateQuestionValue(
                                  "financialAnalysis",
                                  "additionalCriticalIllnessCoverageAmountHKD",
                                  value ?? undefined,
                                  "INPUT",
                                );
                                form.setFieldValue(
                                  "additionalCriticalIllnessCoverageAmountHKD",
                                  value ?? undefined,
                                );
                              }}
                            />
                          </Form.Item>
                        </div>
                        <div className={styles.zuuInlineRadioField}>
                          <Radio
                            value="B"
                            aria-label="USD"
                          />
                          <Form.Item
                            noStyle
                            name="additionalCriticalIllnessCoverageAmountUSD"
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (
                                    normalizedAdditionalCriticalIllnessCoverageAmountOption !==
                                    "B"
                                  ) {
                                    return Promise.resolve();
                                  }
                                  if (
                                    value === undefined ||
                                    value === null ||
                                    value === 0
                                  ) {
                                    return Promise.reject(
                                      new Error("請輸入 USD 金額"),
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <AmountInput
                              style={{ width: "100%" }}
                              placeholder="請輸入"
                              suffix="USD"
                              suffixVariant="box"
                              value={
                                getQuestionValue(
                                  "financialAnalysis",
                                  "additionalCriticalIllnessCoverageAmountUSD",
                                  "INPUT",
                                ) ?? undefined
                              }
                              onChange={(value) => {
                                updateQuestionValue(
                                  "financialAnalysis",
                                  "additionalCriticalIllnessCoverageAmountUSD",
                                  value ?? undefined,
                                  "INPUT",
                                );
                                form.setFieldValue(
                                  "additionalCriticalIllnessCoverageAmountUSD",
                                  value ?? undefined,
                                );
                              }}
                            />
                          </Form.Item>
                        </div>
                      </Radio.Group>
                      )}
                    </Form.Item>
                  </div>
                </>
              ) : null}
            </div>

            <div className={styles.zuuObjectiveOption}>
              <Checkbox value="C" className={styles.zuuObjectiveOptionLabel}>
                C. 為未來提供定期的收入（如退休收入）
              </Checkbox>
            </div>

            <div className={styles.zuuObjectiveOption}>
              <Checkbox value="D" className={styles.zuuObjectiveOptionLabel}>
                D. 為未來需要作儲蓄（如子女教育、退休）
              </Checkbox>
            </div>

            <div className={styles.zuuObjectiveOption}>
              <Checkbox value="E" className={styles.zuuObjectiveOptionLabel}>
                E. 投資
              </Checkbox>
              {selectedObjectives.includes("E") ? (
                <div className={styles.zuuInsetPanel}>
                  <Form.Item
                    label="為實現上述「投資」的目標，閣下希望如何管理保險產品項目下的不同投資選項／投資選擇（如有）？（請選一項）"
                    name="investmentOptionManagementPreference"
                    rules={[
                      {
                        required: true,
                        message:
                          "請回答：為實現上述「投資」的目標，閣下希望如何管理保險產品項目下的不同投資選項／投資選擇（如有）。",
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    {detailDisplay ? (
                      <DetailSplitRow
                        label="為實現上述「投資」的目標，閣下希望如何管理保險產品項目下的不同投資選項／投資選擇（如有）？（請選一項）"
                        value={
                          <DetailValue
                            content={
                              investmentManagementPreferenceLabels[
                                investmentManagementPreference || ""
                              ] || "-"
                            }
                            className={styles.zuuReadonlyInputLike}
                          />
                        }
                      />
                    ) : (
                      <Radio.Group
                        className={styles.zuuFullWidth}
                        value={investmentManagementPreference}
                        onChange={(event) =>
                          updateQuestionValue(
                            "financialAnalysis",
                            "investmentOptionManagementPreference",
                            event.target.value,
                            "RADIO",
                          )
                        }
                      >
                        <div className={styles.zuuOptionColumn}>
                          <Radio
                            value="A"
                            className={styles.zuuLongTextOption}
                          >
                            (i) 本人願意按個人決定（毋須獲授權保險人及／或持牌保險中介人提供任何專業意見的情況）選擇及管理保險產品項下的不同投資選項／投資選擇（如有），並且願意在保險產品的目標利益／保障期的整個期間作出此決定。
                          </Radio>
                          <Radio
                            value="B"
                            className={styles.zuuLongTextOption}
                          >
                            (ii) 本人願意按個人決定（經獲授權保險人及／或持牌保險中介人提供專業意見的情況）選擇及管理保險產品項下的不同投資選項／投資選擇（如有），並且願意在保險產品的目標利益／保障期的整個期間作出此決定。
                          </Radio>
                          <Radio
                            value="C"
                            className={styles.zuuLongTextOption}
                          >
                            (iii) 本人不願意選擇或管理保險產品項目下的不同投資選項／投資選擇（如有）。
                          </Radio>
                        </div>
                      </Radio.Group>
                    )}
                  </Form.Item>
                </div>
              ) : null}
            </div>

            {selectedHasLongTermGoal ? (
              <div className={`${styles.zuuInsetPanel} ${styles.zuuInsetPanelTight}`}>
                <Form.Item
                  label="申請人需要的額外目標儲蓄金額及/ 或額外投資回報金額為？(請選一項)"
                  name="additionalTargetAmountOption"
                  rules={[{ required: true, message: "請選擇一項" }]}
                >
                  {detailDisplay ? (
                    <DetailSplitRow
                      label="申請人需要的額外目標儲蓄金額及/ 或額外投資回報金額為？(請選一項)"
                      value={
                        normalizedAdditionalTargetAmountOption === "A" ? (
                          <DetailValue
                            content={getQuestionValue(
                              "financialAnalysis",
                              "additionalTargetAmountHKD",
                              "INPUT",
                            )}
                            suffix="HKD"
                            className={styles.zuuReadonlyInputLike}
                          />
                        ) : normalizedAdditionalTargetAmountOption === "B" ? (
                          <DetailValue
                            content={getQuestionValue(
                              "financialAnalysis",
                              "additionalTargetAmountUSD",
                              "INPUT",
                            )}
                            suffix="USD"
                            className={styles.zuuReadonlyInputLike}
                          />
                        ) : (
                          <DetailValue content="-" className={styles.zuuReadonlyInputLike} />
                        )
                      }
                    />
                  ) : (
                  <Radio.Group
                    className={styles.zuuCurrencyOptionGrid}
                    value={normalizedAdditionalTargetAmountOption}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      let nextFinancialAnalysis = normalizeZuuFnaQuestionList(
                        data.financialAnalysis || [],
                      );
                      nextFinancialAnalysis = applyQuestionValueToList(
                        nextFinancialAnalysis,
                        "additionalTargetAmountOption",
                        nextValue,
                        "RADIO",
                      );
                      form.setFieldValue("additionalTargetAmountOption", nextValue);

                      if (nextValue !== "A") {
                        form.setFieldValue("additionalTargetAmountHKD", undefined);
                        nextFinancialAnalysis = applyQuestionValueToList(
                          nextFinancialAnalysis,
                          "additionalTargetAmountHKD",
                          undefined,
                          "INPUT",
                        );
                      }

                      if (nextValue !== "B") {
                        form.setFieldValue("additionalTargetAmountUSD", undefined);
                        nextFinancialAnalysis = applyQuestionValueToList(
                          nextFinancialAnalysis,
                          "additionalTargetAmountUSD",
                          undefined,
                          "INPUT",
                        );
                      }

                      onChange({
                        ...data,
                        financialAnalysis: nextFinancialAnalysis,
                      });
                    }}
                  >
                    <div className={styles.zuuInlineRadioField}>
                      <Radio
                        value="A"
                        aria-label="HKD"
                      />
                      <Form.Item
                        noStyle
                        name="additionalTargetAmountHKD"
                        rules={[
                          {
                            validator: (_, value) => {
                              if (
                                normalizedAdditionalTargetAmountOption !== "A"
                              ) {
                                return Promise.resolve();
                              }
                              if (
                                value === undefined ||
                                value === null ||
                                value === 0
                              ) {
                                return Promise.reject(new Error("請輸入 HKD 金額"));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <AmountInput
                          style={{ width: "100%" }}
                          placeholder="請輸入"
                          suffix="HKD"
                          suffixVariant="box"
                          value={
                            getQuestionValue(
                              "financialAnalysis",
                              "additionalTargetAmountHKD",
                              "INPUT",
                            ) ?? undefined
                          }
                          onChange={(value) => {
                            updateQuestionValue(
                              "financialAnalysis",
                              "additionalTargetAmountHKD",
                              value ?? undefined,
                              "INPUT",
                            );
                            form.setFieldValue(
                              "additionalTargetAmountHKD",
                              value ?? undefined,
                            );
                          }}
                        />
                      </Form.Item>
                    </div>
                    <div className={styles.zuuInlineRadioField}>
                      <Radio
                        value="B"
                        aria-label="USD"
                      />
                      <Form.Item
                        noStyle
                        name="additionalTargetAmountUSD"
                        rules={[
                          {
                            validator: (_, value) => {
                              if (
                                normalizedAdditionalTargetAmountOption !== "B"
                              ) {
                                return Promise.resolve();
                              }
                              if (
                                value === undefined ||
                                value === null ||
                                value === 0
                              ) {
                                return Promise.reject(new Error("請輸入 USD 金額"));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <AmountInput
                          style={{ width: "100%" }}
                          placeholder="請輸入"
                          suffix="USD"
                          suffixVariant="box"
                          value={
                            getQuestionValue(
                              "financialAnalysis",
                              "additionalTargetAmountUSD",
                              "INPUT",
                            ) ?? undefined
                          }
                          onChange={(value) => {
                            updateQuestionValue(
                              "financialAnalysis",
                              "additionalTargetAmountUSD",
                              value ?? undefined,
                              "INPUT",
                            );
                            form.setFieldValue(
                              "additionalTargetAmountUSD",
                              value ?? undefined,
                            );
                          }}
                        />
                      </Form.Item>
                    </div>
                  </Radio.Group>
                  )}
                </Form.Item>

                <Form.Item
                  className={styles.zuuQuestionSectionBreak}
                  label="您預期以多少年達到所述之目標儲蓄金額/ 投資回報金額?"
                  name="additionalTargetYears"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value === undefined || value === null || value === "") {
                          return Promise.reject(new Error("請輸入預期年限"));
                        }
                        if (!Number.isInteger(Number(value)) || Number(value) < 0) {
                          return Promise.reject(new Error("只可輸入非負整數"));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  {detailDisplay ? (
                    <DetailSplitRow
                      label="您預期以多少年達到所述之目標儲蓄金額/ 投資回報金額?"
                      value={
                        <DetailValue
                          content={getQuestionValue(
                            "financialAnalysis",
                            "additionalTargetYears",
                            "INPUT",
                          )}
                          suffix="年"
                          className={styles.zuuReadonlyInputLike}
                        />
                      }
                    />
                  ) : (
                    <AmountInput
                      style={{ minWidth: 220 }}
                      placeholder="請輸入"
                      suffix="年"
                      suffixVariant="box"
                      value={(() => {
                        const currentValue = getQuestionValue(
                          "financialAnalysis",
                          "additionalTargetYears",
                          "INPUT",
                        );
                        if (
                          currentValue === undefined ||
                          currentValue === null ||
                          currentValue === ""
                        ) {
                          return undefined;
                        }
                        const parsedValue = Number(currentValue);
                        return Number.isNaN(parsedValue)
                          ? undefined
                          : parsedValue;
                      })()}
                      onChange={(value) => {
                        const normalizedValue =
                          value === undefined || value === null
                            ? ""
                            : String(Math.max(0, Math.trunc(value)));
                        updateQuestionValue(
                          "financialAnalysis",
                          "additionalTargetYears",
                          normalizedValue,
                          "INPUT",
                        );
                        form.setFieldValue(
                          "additionalTargetYears",
                          normalizedValue || undefined,
                        );
                      }}
                    />
                  )}
                </Form.Item>
              </div>
            ) : null}

            <div className={styles.zuuObjectiveOption}>
              <Checkbox value="F" className={styles.zuuObjectiveOptionLabel}>
                F. 其他
              </Checkbox>
              {selectedObjectives.includes("F") ? (
                <div className={styles.zuuInsetPanel}>
                  <Form.Item
                    name="insurancePurchaseOtherObjectiveOptions"
                    rules={[{ required: true, message: "請選擇一項" }]}
                    style={{ marginBottom: 12 }}
                  >
                    {detailDisplay ? (
                      <DetailSplitRow
                        label="其他"
                        value={
                          <DetailValue
                            content={
                              otherObjectiveOptionLabels[otherObjectiveOptionValue || ""] ||
                              "-"
                            }
                            className={styles.zuuReadonlyInputLike}
                          />
                        }
                      />
                    ) : (
                    <Radio.Group
                      className={styles.zuuOptionGrid}
                      value={otherObjectiveOptionValue}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        const currentFinancialAnalysis = normalizeZuuFnaQuestionList(
                          data.financialAnalysis || [],
                        );
                        const optionIndex = findQuestionIndex(
                          currentFinancialAnalysis,
                          "insurancePurchaseOtherObjectiveOptions",
                        );

                        if (optionIndex >= 0) {
                          currentFinancialAnalysis[optionIndex] = {
                            ...currentFinancialAnalysis[optionIndex],
                            question: getZuuFnaCanonicalQuestionKey(
                              "insurancePurchaseOtherObjectiveOptions",
                            ),
                            type: "RADIO",
                            items: [{ key: nextValue, value: true }],
                          };
                        } else {
                          currentFinancialAnalysis.push({
                            question: getZuuFnaCanonicalQuestionKey(
                              "insurancePurchaseOtherObjectiveOptions",
                            ),
                            type: "RADIO",
                            items: [{ key: nextValue, value: true }],
                          });
                        }

                        if (nextValue !== "E") {
                          const textIndex = findQuestionIndex(
                            currentFinancialAnalysis,
                            "insurancePurchaseOtherObjectives",
                          );
                          if (textIndex >= 0) {
                            currentFinancialAnalysis[textIndex] = {
                              ...currentFinancialAnalysis[textIndex],
                              question: getZuuFnaCanonicalQuestionKey(
                                "insurancePurchaseOtherObjectives",
                              ),
                              type: "INPUT",
                              items: [],
                            };
                          }
                          form.setFieldValue(
                            "insurancePurchaseOtherObjectives",
                            undefined,
                          );
                        }

                        form.setFieldValue(
                          "insurancePurchaseOtherObjectiveOptions",
                          nextValue,
                        );
                        onChange({
                          ...data,
                          financialAnalysis: currentFinancialAnalysis,
                        });
                      }}
                    >
                      <Radio value="A">a. 付款人保障</Radio>
                      <Radio value="B">b. 付款人保障連危疾</Radio>
                      <Radio value="C">c. 免付保費</Radio>
                      <Radio value="D">d. 遺產規劃</Radio>
                      <Radio value="E">e. 其他</Radio>
                    </Radio.Group>
                    )}
                  </Form.Item>
                  {otherObjectiveOptionValue === "E" ? (
                    <Form.Item
                      className={styles.zuuQuestionSectionBreak}
                      label="e. 其他，請詳述："
                      name="insurancePurchaseOtherObjectives"
                      rules={[{ required: true, message: "請輸入詳細內容" }]}
                      style={{ marginBottom: 0 }}
                    >
                      {detailDisplay ? (
                        <DetailSplitRow
                          label="e. 其他，請詳述："
                          value={
                            <DetailValue
                              content={getQuestionValue(
                                "financialAnalysis",
                                "insurancePurchaseOtherObjectives",
                                "INPUT",
                              )}
                              className={styles.zuuReadonlyInputLike}
                            />
                          }
                        />
                      ) : (
                        <Input
                          placeholder="請輸入"
                          value={getQuestionValue(
                            "financialAnalysis",
                            "insurancePurchaseOtherObjectives",
                            "INPUT",
                          )}
                          onChange={(event) =>
                            updateQuestionValue(
                              "financialAnalysis",
                              "insurancePurchaseOtherObjectives",
                              event.target.value,
                              "INPUT",
                            )
                          }
                        />
                      )}
                    </Form.Item>
                  ) : null}
                </div>
              ) : null}
            </div>
          </Checkbox.Group>

        </Form.Item>

        <div className={styles.zuuTopLevelQuestionBlock}>
          {detailDisplay ? (
            <DetailSplitRow
              label="2. 在整個保單期內，閣下能夠及願意繳付的保費（不包括閣下現有的其他保單）佔透過所有收入來源（包括流動資產收入）獲得的每月可動用收入（即經扣除開支）的比率為？（請選一項）"
              value={
                <DetailValue
                  content={
                    premiumRatioOptionLabels[premiumToDisposableIncomeRatio || ""] || "-"
                  }
                  className={styles.zuuReadonlyInputLike}
                />
              }
            />
          ) : (
            <Form.Item
              label="2. 在整個保單期內，閣下能夠及願意繳付的保費（不包括閣下現有的其他保單）佔透過所有收入來源（包括流動資產收入）獲得的每月可動用收入（即經扣除開支）的比率為？（請選一項）"
              name="premiumToDisposableIncomeRatio"
              rules={[{ required: true }]}
            >
              <Radio.Group
                className={styles.zuuOptionGrid}
                value={getQuestionValue(
                  "financialAnalysis",
                  "premiumToDisposableIncomeRatio",
                  "RADIO",
                )}
                onChange={(event) =>
                  updateQuestionValue(
                    "financialAnalysis",
                    "premiumToDisposableIncomeRatio",
                    event.target.value,
                    "RADIO",
                  )
                }
              >
                <Radio value="A">≤10%</Radio>
                <Radio value="B">11%-20%</Radio>
                <Radio value="C">21%-40%</Radio>
                <Radio value="D">41%-60%</Radio>
                <Radio value="E">61%-80%</Radio>
                <Radio value="F">81%-100%</Radio>
              </Radio.Group>
            </Form.Item>
          )}
        </div>

        <div className={styles.zuuTopLevelQuestionBlock}>
          {detailDisplay ? (
            <DetailSplitRow
              label="3. 在整個保單期內，閣下能夠及願意繳付的保費（不包括閣下現有的其他保單）佔流動資產比率為？（請選一項）"
              value={
                <DetailValue
                  content={
                    premiumRatioOptionLabels[premiumToLiquidAssetsRatio || ""] || "-"
                  }
                  className={styles.zuuReadonlyInputLike}
                />
              }
            />
          ) : (
            <Form.Item
              label="3. 在整個保單期內，閣下能夠及願意繳付的保費（不包括閣下現有的其他保單）佔流動資產比率為？（請選一項）"
              name="premiumToLiquidAssetsRatio"
              rules={[{ required: true }]}
            >
              <Radio.Group
                className={styles.zuuOptionGrid}
                value={getQuestionValue(
                  "financialAnalysis",
                  "premiumToLiquidAssetsRatio",
                  "RADIO",
                )}
                onChange={(event) =>
                  updateQuestionValue(
                    "financialAnalysis",
                    "premiumToLiquidAssetsRatio",
                    event.target.value,
                    "RADIO",
                  )
                }
              >
                <Radio value="A">≤10%</Radio>
                <Radio value="B">11%-20%</Radio>
                <Radio value="C">21%-40%</Radio>
                <Radio value="D">41%-60%</Radio>
                <Radio value="E">61%-80%</Radio>
                <Radio value="F">81%-100%</Radio>
              </Radio.Group>
            </Form.Item>
          )}
        </div>

        <div className={styles.zuuTopLevelQuestionBlock}>
          {detailDisplay ? (
            <DetailSplitRow
              label="4. 閣下能夠及願意為保單支付保費的年期為？（請選一項）"
              value={
                <DetailValue
                  content={
                    paymentPeriodOptionLabels[premiumPaymentPeriodRange || ""] || "-"
                  }
                  className={styles.zuuReadonlyInputLike}
                />
              }
            />
          ) : (
            <Form.Item
              label="4. 閣下能夠及願意為保單支付保費的年期為？（請選一項）"
              name="willingPremiumPaymentPeriodRange"
              rules={[{ required: true }]}
            >
              <Radio.Group
                className={styles.zuuOptionGrid}
                value={premiumPaymentPeriodRange}
                onChange={(event) => {
                  const newValue = event.target.value;
                  let nextFinancialAnalysis = normalizeZuuFnaQuestionList(
                    data.financialAnalysis || [],
                  );
                  nextFinancialAnalysis = applyQuestionValueToList(
                    nextFinancialAnalysis,
                    "willingPremiumPaymentPeriodRange",
                    newValue,
                    "RADIO",
                  );
                  form.setFieldValue("willingPremiumPaymentPeriodRange", newValue);

                  if (newValue !== "G") {
                    nextFinancialAnalysis = applyQuestionValueToList(
                      nextFinancialAnalysis,
                      "singlePremiumPaymentLimit",
                      undefined,
                      "INPUT",
                    );
                    form.setFieldValue("singlePremiumPaymentLimit", undefined);
                  }

                  onChange({
                    ...data,
                    financialAnalysis: nextFinancialAnalysis,
                  });
                }}
              >
                <Radio value="A">2-5 年</Radio>
                <Radio value="B">6-10 年</Radio>
                <Radio value="C">11-15 年</Radio>
                <Radio value="D">16-20 年</Radio>
                <Radio value="E">超過20年</Radio>
                <Radio value="F">終身</Radio>
              </Radio.Group>
              <div className={styles.zuuOneTimePremiumRow}>
                <Radio
                  className={styles.zuuOneTimePremiumPrefix}
                  checked={premiumPaymentPeriodRange === "G"}
                  onChange={() => {
                    updateQuestionValue(
                      "financialAnalysis",
                      "willingPremiumPaymentPeriodRange",
                      "G",
                      "RADIO",
                    );
                    form.setFieldValue("willingPremiumPaymentPeriodRange", "G");
                  }}
                >
                  不超過
                </Radio>
                <AmountInput
                  placeholder="請輸入金額"
                  suffix="HKD"
                  suffixVariant="box"
                  value={
                    getQuestionValue(
                      "financialAnalysis",
                      "singlePremiumPaymentLimit",
                      "INPUT",
                    ) ?? undefined
                  }
                  onChange={(value) => {
                    if (premiumPaymentPeriodRange !== "G") {
                      updateQuestionValue(
                        "financialAnalysis",
                        "willingPremiumPaymentPeriodRange",
                        "G",
                        "RADIO",
                      );
                      form.setFieldValue("willingPremiumPaymentPeriodRange", "G");
                    }

                    updateQuestionValue(
                      "financialAnalysis",
                      "singlePremiumPaymentLimit",
                      value ?? undefined,
                      "INPUT",
                    );
                    form.setFieldValue(
                      "singlePremiumPaymentLimit",
                      value ?? undefined,
                    );
                  }}
                />
                <span className={styles.zuuOneTimePremiumSuffix}>
                  的一次性供款
                </span>
              </div>
            </Form.Item>
          )}

        </div>

        <div className={styles.zuuTopLevelQuestionBlock}>
          {detailDisplay ? (
            <DetailSplitRow
              label="5. 您投保保單的目標收益／保障年期／實現目標金額的預期時間為多久？（請選一項）"
              value={
                <DetailValue
                  content={
                    policyTargetExpectedTimeLabels[policyTargetExpectedTime || ""] || "-"
                  }
                  className={styles.zuuReadonlyInputLike}
                />
              }
            />
          ) : (
            <Form.Item
              label="5. 您投保保單的目標收益／保障年期／實現目標金額的預期時間為多久？（請選一項）"
              name="policyTargetExpectedTime"
              rules={[{ required: true }]}
            >
              <Radio.Group
                className={styles.zuuOptionGrid}
                value={getQuestionValue(
                  "financialAnalysis",
                  "policyTargetExpectedTime",
                  "RADIO",
                )}
                onChange={(event) =>
                  updateQuestionValue(
                    "financialAnalysis",
                    "policyTargetExpectedTime",
                    event.target.value,
                    "RADIO",
                  )
                }
              >
                <Radio value="A">A. &lt; 1 年</Radio>
                <Radio value="B">B. 1-5年</Radio>
                <Radio value="C">C. 6-10年</Radio>
                <Radio value="D">D. 11-15年</Radio>
                <Radio value="E">E. 16-20年</Radio>
                <Radio value="F">F. &gt; 20 年</Radio>
                <Radio value="G">G. 終身</Radio>
              </Radio.Group>
            </Form.Item>
          )}
        </div>
      </div>
    </CustomCard>
  );
}

function ZuuPremiumFinancingSection({
  data,
  onChange,
  readOnly = false,
  detailDisplay = false,
}: ZuuPremiumFinancingSectionProps & { detailDisplay?: boolean }) {
  const syncPremiumMappedFields = (nextData: OrderFinancialVO): OrderFinancialVO => {
    const firstPremium = (nextData.premiumFinancing || [])[0] || {};

    return {
      ...nextData,
      premiumFinancingRepaymentSource_A:
        nextData.premiumFinancingRepaymentSources || [],
      premiumFinancingRepaymentSource_D_remark:
        nextData.premiumFinancingRepaymentSourceOther ?? "",
      outstandingMortgageAmount:
        nextData.outstandingMortgageLoanAmount ?? "",
      outstandingOtherLoansAmount: nextData.outstandingOtherLoanAmount ?? "",
      willingToProvidePremiumFinancingInfo_A:
        nextData.premiumFinancingEstimateConsent === true,
      willingToProvidePremiumFinancingInfo_B:
        nextData.premiumFinancingEstimateConsent === false,
      willingToProvidePremiumFinancingInfo_A_nameOfLender:
        firstPremium.lenderName ?? "",
      willingToProvidePremiumFinancingInfo_A_loanAmount:
        firstPremium.loanAmount ?? "",
      willingToProvidePremiumFinancingInfo_A_loanInterestRate:
        firstPremium.loanInterestRate ?? "",
      willingToProvidePremiumFinancingInfo_A_loanTenor:
        firstPremium.loanTerm ?? "",
      willingToProvidePremiumFinancingInfo_A_eachInstallment:
        firstPremium.repaymentAmount ?? "",
      confirmedImportantNotes:
        !!nextData.premiumFinancingImportantDisclosureConfirmed,
      hasSufficientFundsToRepayLoan:
        !!nextData.premiumFinancingRepaymentConfirmed,
    };
  };

  const applyNext = (patch: Partial<OrderFinancialVO>) => {
    onChange(syncPremiumMappedFields({ ...data, ...patch }));
  };

  const buildPremiumFinancingDisabledPatch = (): Partial<OrderFinancialVO> => ({
    isPremiumFinanced: false,
    premiumFinancingRepaymentSources: [],
    premiumFinancingRepaymentSourceOther: undefined,
    outstandingMortgageLoanAmount: undefined,
    outstandingOtherLoanAmount: undefined,
    premiumFinancingEstimateConsent: undefined,
    premiumFinancing: [],
    premiumFinancingImportantDisclosureConfirmed: false,
    premiumFinancingRepaymentConfirmed: false,
  });

  const updatePremiumItem = (field: string, value: any) => {
    const nextList = [...(data.premiumFinancing || [])];
    if (!nextList[0]) {
      nextList[0] = {};
    }
    nextList[0] = {
      ...nextList[0],
      [field]: value,
    };
    applyNext({ premiumFinancing: nextList });
  };

  const premiumFinancingItem = (data.premiumFinancing || [])[0] || {};
  const repaymentSource = data.premiumFinancingRepaymentSources?.[0];
  const isPremiumFinancingEnabled = data.isPremiumFinanced ?? false;
  const premiumFinancingEstimateConsentValue =
    data.premiumFinancingEstimateConsent === true
      ? "A"
      : data.premiumFinancingEstimateConsent === false
        ? "B"
        : undefined;
  const onRepaymentSourceChange = (value?: string) => {
    const nextValues = value ? [value] : [];

    const nextData: OrderFinancialVO = {
      ...data,
      premiumFinancingRepaymentSources: nextValues,
    };
    if (value !== "D") {
      nextData.premiumFinancingRepaymentSourceOther = undefined;
    }
    onChange(syncPremiumMappedFields(nextData));
  };
  const currencyOptions = [
    { label: "HKD", value: "HKD" },
    { label: "USD", value: "USD" },
  ];
  const repaymentSourceLabels: Record<string, string> = {
    A: "儲蓄",
    B: "薪俸",
    C: "流動資產",
    D: "其他",
  };
  const premiumEstimateLabels: Record<string, string> = {
    A: "閣下願意盡閣下所知而提供的保費融資貸款預估資料如下：",
    B: "閣下不願意提供保費融資貸款的預估資料。閣下明白ZUU將無法評估閣下是否適合及是否有負擔能力使用保費融資，並將假設不使用保費融資以評估閣下是否適合及是否有負擔能力購買此保單（即全部所需保費均由閣下承擔）。",
  };
  const premiumLoanAmountValue =
    typeof premiumFinancingItem.loanAmount === "number" &&
    Number.isFinite(premiumFinancingItem.loanAmount)
      ? premiumFinancingItem.loanAmount
      : typeof premiumFinancingItem.loanAmount === "string" &&
          premiumFinancingItem.loanAmount.trim() !== ""
        ? (() => {
            const parsed = Number(
              premiumFinancingItem.loanAmount.replace(/,/g, ""),
            );
            return Number.isFinite(parsed) ? parsed : undefined;
          })()
        : undefined;
  const premiumEstimateDetailPanel = (
    <div className={`${styles.zuuInsetPanel} ${styles.zuuPremiumEstimatePanel}`}>
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item label="貸方名稱" name="premiumFinancingLenderName">
            {detailDisplay ? (
              <DetailValue content={premiumFinancingItem.lenderName} />
            ) : (
              <Input
                placeholder="請輸入"
                value={premiumFinancingItem.lenderName}
                onChange={(event) =>
                  updatePremiumItem("lenderName", event.target.value)
                }
              />
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="貸款金額（請註明貨幣）" name="premiumFinancingLoanAmount">
            {detailDisplay ? (
              <DetailValue
                content={premiumFinancingItem.loanAmount}
                suffix={premiumFinancingItem.loanAmountCurrency || "HKD"}
              />
            ) : (
              <div className={styles.zuuMoneyWithCurrency}>
                <AmountInput
                  style={{ width: "100%" }}
                  placeholder="請輸入"
                  value={premiumLoanAmountValue}
                  onChange={(value) => {
                    if (value === undefined) {
                      updatePremiumItem("loanAmount", undefined);
                      return;
                    }
                    if (value <= 0) {
                      updatePremiumItem("loanAmount", undefined);
                      return;
                    }
                    updatePremiumItem("loanAmount", value);
                  }}
                />
                <SelectInput
                  options={currencyOptions}
                  value={premiumFinancingItem.loanAmountCurrency || "HKD"}
                  onChange={(value) =>
                    updatePremiumItem("loanAmountCurrency", value)
                  }
                  title="貨幣"
                  readOnly={readOnly}
                />
              </div>
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="貸款利率（如參考利率+X%）" name="premiumFinancingLoanInterestRate">
            {detailDisplay ? (
              <DetailValue content={premiumFinancingItem.loanInterestRate} />
            ) : (
              <Input
                placeholder="請選擇"
                value={premiumFinancingItem.loanInterestRate}
                onChange={(event) =>
                  updatePremiumItem("loanInterestRate", event.target.value)
                }
              />
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="貸款期（如分期 120 個月）" name="premiumFinancingLoanTerm">
            {detailDisplay ? (
              <DetailValue content={premiumFinancingItem.loanTerm} />
            ) : (
              <Input
                placeholder="請輸入"
                value={premiumFinancingItem.loanTerm}
                onChange={(event) =>
                  updatePremiumItem("loanTerm", event.target.value)
                }
              />
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="每期還款金額（如每月 港元）" name="premiumFinancingRepaymentAmount">
            {detailDisplay ? (
              <DetailValue content={premiumFinancingItem.repaymentAmount} />
            ) : (
              <Input
                placeholder="請選擇"
                value={premiumFinancingItem.repaymentAmount}
                onChange={(event) =>
                  updatePremiumItem("repaymentAmount", event.target.value)
                }
              />
            )}
          </Form.Item>
        </Col>
      </Row>

      <div className={styles.zuuPremiumWarningText}>
        申請人 / 投保人請注意：資遇保險經紀有限公司將使用閣下擬申請的保費融資貸款預估資料，以評估閣下是否適合及是否有負擔能力使用保費融資以獲取資金購買此保單。我們強烈建議閣下盡閣下所知而提供相關資料。如貸款方最終提供的貸款條款及細則遜於以下資料(例如較高的貸款利率)，閣下應立即聯絡ZUU以重新評估閣下是否適合及是否有負擔能力使用保費融資以獲取資金購買此保單。
      </div>
    </div>
  );

  return (
    <CustomCard
      title={
        <span className={styles.zuuSectionTitleWithNote}>
          保費融資
          <span className={styles.zuuSectionTitleNote}>
            如果閣下不打算進行保費融資，則不需回答此部份。
          </span>
        </span>
      }
      extra={
        detailDisplay ? null : (
          <Switch
            checked={isPremiumFinancingEnabled}
            disabled={readOnly}
            className={styles.zuuPremiumSwitch}
            checkedChildren={<span className={styles.zuuPremiumSwitchCheck} />}
            unCheckedChildren={null}
            onChange={(checked) =>
              applyNext(
                checked
                  ? {
                      isPremiumFinanced: true,
                      premiumFinancingImportantDisclosureConfirmed: false,
                      premiumFinancingRepaymentConfirmed: false,
                    }
                  : buildPremiumFinancingDisabledPatch(),
              )
            }
          />
        )
      }
      className={`${styles.sectionCard} ${styles.zuuSectionCard}`}
    >
      <div className={styles.zuuSectionContent}>
        {isPremiumFinancingEnabled ? (
          <>
            <Form.Item
              label="6.1 還款來源"
              className={styles.zuuQuestionSectionBreak}
              name="premiumFinancingRepaymentSource"
            >
              {detailDisplay ? (
                <DetailSplitRow
                  label="6.1 還款來源"
                  value={
                    <div>
                      <DetailValue
                        content={repaymentSourceLabels[repaymentSource || ""]}
                        className={styles.zuuReadonlyInputLike}
                      />
                      {repaymentSource === "D" ? (
                        <DetailValue
                          content={data.premiumFinancingRepaymentSourceOther}
                          className={styles.zuuReadonlyInputLike}
                        />
                      ) : null}
                    </div>
                  }
                />
              ) : (
                <div className={styles.zuuRepaymentSourceGrid}>
                  <Radio
                    checked={repaymentSource === "B"}
                    onChange={() => onRepaymentSourceChange("B")}
                  >
                    薪俸
                  </Radio>
                  <Radio
                    checked={repaymentSource === "A"}
                    onChange={() => onRepaymentSourceChange("A")}
                  >
                    儲蓄
                  </Radio>
                  <Radio
                    checked={repaymentSource === "C"}
                    onChange={() => onRepaymentSourceChange("C")}
                  >
                    流動資產
                  </Radio>
                  <div className={styles.zuuRepaymentSourceOther}>
                    <Radio
                      checked={repaymentSource === "D"}
                      onChange={() => onRepaymentSourceChange("D")}
                    >
                      其他
                    </Radio>
                    {repaymentSource === "D" ? (
                      <Form.Item
                        name="premiumFinancingRepaymentSourceOther"
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          placeholder="請輸入"
                          className={styles.zuuRepaymentSourceOtherInput}
                          value={data.premiumFinancingRepaymentSourceOther}
                          onChange={(event) =>
                            applyNext({
                              premiumFinancingRepaymentSourceOther:
                                event.target.value,
                            })
                          }
                        />
                      </Form.Item>
                    ) : null}
                  </div>
                </div>
              )}
            </Form.Item>

            <div className={styles.zuuQuestionSectionBreak}>
              <div className={styles.zuuFormLabelText}>6.2 未償還貸款</div>
              {detailDisplay ? (
                <>
                  <DetailSplitRow
                    label={<span className={styles.zuuOptionLikeText}>a. 閣下未償還按揭貸款 不超過</span>}
                    value={
                      <DetailValue
                        content={data.outstandingMortgageLoanAmount}
                        suffix="HKD"
                        className={styles.zuuReadonlyInputLike}
                      />
                    }
                  />
                  <DetailSplitRow
                    label={<span className={styles.zuuOptionLikeText}>b. 閣下其他未償還貸款（不包括 Q 6.3 和 按揭貸款）不超過</span>}
                    value={
                      <DetailValue
                        content={data.outstandingOtherLoanAmount}
                        suffix="HKD"
                        className={styles.zuuReadonlyInputLike}
                      />
                    }
                  />
                </>
              ) : (
                <>
                  <Form.Item
                    label={
                      <span className={styles.zuuOptionLikeText}>
                        a. 閣下未償還按揭貸款 不超過
                      </span>
                    }
                    className={styles.zuuQuestionSectionBreak}
                    name="outstandingMortgageLoanAmount"
                  >
                  <AmountInput
                    placeholder="請輸入"
                    suffix="HKD"
                    suffixVariant="box"
                    value={data.outstandingMortgageLoanAmount}
                    onChange={(value) =>
                      applyNext({ outstandingMortgageLoanAmount: value })
                    }
                  />
                  </Form.Item>
                  <Form.Item
                    label={
                      <span className={styles.zuuOptionLikeText}>
                        b. 閣下其他未償還貸款（不包括 Q 6.3 和 按揭貸款）不超過
                      </span>
                    }
                    name="outstandingOtherLoanAmount"
                  >
                  <AmountInput
                    placeholder="請輸入"
                    suffix="HKD"
                    suffixVariant="box"
                    value={data.outstandingOtherLoanAmount}
                    onChange={(value) =>
                      applyNext({ outstandingOtherLoanAmount: value })
                    }
                  />
                  </Form.Item>
                </>
              )}
              <div className={styles.zuuNotePanel}>
                備註 *: 其他未償還貸款包括但不限於個人貸款和信用卡餘額 (但不包括 閣下正投保的保單中的擬保費融資工具所產生的貸款金額和利息)
              </div>
            </div>

            <div className={styles.zuuQuestionSectionBreakLarge}>
              <div className={styles.zuuFormLabelText}>6.3 本息償還</div>
              <Form.Item
                className={styles.zuuQuestionSectionBreak}
                name="premiumFinancingEstimateConsent"
              >
                {detailDisplay ? (
                  <DetailSplitRow
                    label="6.3 本息償還"
                    value={
                      <DetailValue
                        content={premiumEstimateLabels[premiumFinancingEstimateConsentValue || ""]}
                        className={styles.zuuReadonlyInputLike}
                      />
                    }
                  />
                ) : (
                  <Radio.Group
                    value={premiumFinancingEstimateConsentValue}
                    onChange={(event) => {
                      const consent =
                        event.target.value === "A"
                          ? true
                          : event.target.value === "B"
                            ? false
                            : undefined;

                      applyNext({
                        premiumFinancingEstimateConsent: consent,
                        // Align with prototype linkage: when user refuses to provide
                        // estimate details, hidden financing detail fields must be cleared.
                        premiumFinancing: consent === false ? [] : data.premiumFinancing,
                      });
                    }}
                  >
                    <div style={checkboxGroupStyle}>
                      <Radio value="A">
                        <span className={`${styles.zuuOptionLikeText} ${styles.zuuBoldOptionText}`}>
                          閣下願意盡閣下所知而提供的保費融資貸款預估資料如下：
                        </span>
                      </Radio>
                      {premiumFinancingEstimateConsentValue === "A"
                        ? premiumEstimateDetailPanel
                        : null}
                      <Radio value="B">
                        <span className={styles.zuuOptionLikeText}>
                          閣下不願意提供保費融資貸款的預估資料。閣下明白ZUU將無法評估閣下是否適合及是否有負擔能力使用保費融資，並將假設不使用保費融資以評估閣下是否適合及是否有負擔能力購買此保單（即全部所需保費均由閣下承擔）。
                        </span>
                      </Radio>
                    </div>
                  </Radio.Group>
                )}
              </Form.Item>

              {detailDisplay && premiumFinancingEstimateConsentValue === "A"
                ? premiumEstimateDetailPanel
                : null}
            </div>

            <div className={`${styles.zuuTopLevelQuestionBlock} ${styles.zuuTopLevelQuestionBlockCompact}`}>
              {detailDisplay ? (
                <>
                  {data.premiumFinancingImportantDisclosureConfirmed ? (
                    <DetailSplitRow
                      label="6.4"
                      value={
                        <DetailValue
                          content="閣下確認所附的“重要說明及影響”已由 ZUU 向您解釋，並且您已閱讀並理解其內容。"
                          className={styles.zuuReadonlyInputLike}
                        />
                      }
                    />
                  ) : null}
                  {data.premiumFinancingRepaymentConfirmed ? (
                    <DetailSplitRow
                      label="6.5"
                      value={
                        <DetailValue
                          content="閣下確認有足夠的資金在保單到期前全額償還貸款人要求的貸款，而不是使用建議保單的退保價值作償還。**"
                          className={styles.zuuReadonlyInputLike}
                        />
                      }
                    />
                  ) : null}
                </>
              ) : (
                <>
                  <Form.Item
                    className={styles.zuuCheckboxTopAlign}
                    name="premiumFinancingImportantDisclosureConfirmed"
                    valuePropName="checked"
                  >
                    <Checkbox
                      checked={!!data.premiumFinancingImportantDisclosureConfirmed}
                      onChange={(event) =>
                        applyNext({
                          premiumFinancingImportantDisclosureConfirmed:
                            event.target.checked,
                        })
                      }
                    >
                      <span className={`${styles.zuuOptionLikeText} ${styles.zuuBoldOptionText}`}>
                        6.4 閣下確認所附的“重要說明及影響”已由 ZUU 向您解釋，並且您已閱讀並理解其內容。
                      </span>
                    </Checkbox>
                  </Form.Item>

                  <Form.Item
                    className={`${styles.zuuQuestionSectionBreak} ${styles.zuuCheckboxTopAlign}`}
                    name="premiumFinancingRepaymentConfirmed"
                    valuePropName="checked"
                  >
                    <Checkbox
                      checked={!!data.premiumFinancingRepaymentConfirmed}
                      onChange={(event) =>
                        applyNext({
                          premiumFinancingRepaymentConfirmed: event.target.checked,
                        })
                      }
                    >
                      <span className={`${styles.zuuOptionLikeText} ${styles.zuuBoldOptionText}`}>
                        6.5 閣下確認有足夠的資金在保單到期前全額償還貸款人要求的貸款，而不是使用建議保單的退保價值作償還。**
                      </span>
                    </Checkbox>
                  </Form.Item>
                </>
              )}
            </div>

            <div className={styles.zuuDangerNote}>
              備註：** 如無任何確認，ZUU 將不推薦此保單。
            </div>
          </>
        ) : null}
      </div>
    </CustomCard>
  );
}

function ZuuProductSelectionSection({
  data,
  onChange,
  readOnly = false,
  productSectionRef,
  productValidation,
  getQuestionValue,
  onOpenSelector,
  onMessageError,
  detailDisplay = false,
}: ZuuProductSelectionSectionProps & { detailDisplay?: boolean }) {
  const newChooseProduct = {
    productCode: null,
    choose: false,
  };
  const applyChooseProducts = (nextSelection: any[]) => {
    const selectedProducts = nextSelection.filter((product) => !!product?.choose);
    const allSelectedHaveCompany = selectedProducts.every(
      (product) => !!product?.companyCode,
    );
    const uniqueCompanies = new Set(
      selectedProducts.map((product) => product.companyCode).filter(Boolean),
    );
    const shouldOnlyProvideOneProduct =
      selectedProducts.length > 0 &&
      allSelectedHaveCompany &&
      uniqueCompanies.size === 1;

    onChange({
      ...data,
      chooseProducts: nextSelection,
      onlyProvideOneProduct: shouldOnlyProvideOneProduct,
      onlyProvideOneProductReason: shouldOnlyProvideOneProduct
        ? data.onlyProvideOneProductReason
        : undefined,
    });
  };

  useEffect(() => {
    const selectedProducts = (data.chooseProducts || []).filter(
      (product) => !!product?.choose,
    );
    const allSelectedHaveCompany = selectedProducts.every(
      (product) => !!product?.companyCode,
    );
    const uniqueCompanies = new Set(
      selectedProducts
        .map((product) => product.companyCode)
        .filter(Boolean),
    );
    const shouldOnlyProvideOneProduct =
      selectedProducts.length > 0 &&
      allSelectedHaveCompany &&
      uniqueCompanies.size === 1;

    if (
      data.onlyProvideOneProduct !== shouldOnlyProvideOneProduct ||
      (!shouldOnlyProvideOneProduct && data.onlyProvideOneProductReason)
    ) {
      onChange({
        ...data,
        onlyProvideOneProduct: shouldOnlyProvideOneProduct,
        onlyProvideOneProductReason: shouldOnlyProvideOneProduct
          ? data.onlyProvideOneProductReason
          : undefined,
      });
    }
  }, [
    data,
    onChange,
    data.chooseProducts,
    data.onlyProvideOneProduct,
    data.onlyProvideOneProductReason,
  ]);

  useEffect(() => {
    if (readOnly || detailDisplay) {
      return;
    }

    if (!data.chooseProducts || data.chooseProducts.length === 0) {
      applyChooseProducts([{ ...newChooseProduct }]);
    }
  }, [data, readOnly, detailDisplay]);

  const getDerivedFields = () => {
    const selectedObjectives =
      getQuestionValue(
        "financialAnalysis",
        "insurancePurchaseObjectives",
        "CHECKBOX",
      ) || [];

    const objectiveLabels: string[] = [];
    if (selectedObjectives.includes("A")) {
      objectiveLabels.push("為應付不時之需提供財務保障");
    }
    if (selectedObjectives.includes("B")) {
      objectiveLabels.push("為醫療需要作準備");
    }
    if (selectedObjectives.includes("C")) {
      objectiveLabels.push("為未來提供定期的收入");
    }
    if (selectedObjectives.includes("D")) {
      objectiveLabels.push("為未來需要做儲蓄");
    }
    if (selectedObjectives.includes("E")) {
      objectiveLabels.push("投資");
    }
    if (selectedObjectives.includes("F")) {
      objectiveLabels.push("其他");
    }
    const objective = objectiveLabels.join("、");

    const paymentRange = getQuestionValue(
      "financialAnalysis",
      "willingPremiumPaymentPeriodRange",
      "RADIO",
    );
    let paymentTerm = "";

    if (paymentRange) {
      const paymentTermMap: Record<string, string> = {
        A: "2-5年",
        B: "6-10年",
        C: "11-15年",
        D: "16-20年",
        E: "超過20年",
        F: "終身",
      };

      if (paymentRange === "G") {
        const singlePaymentLimit = getQuestionValue(
          "financialAnalysis",
          "singlePremiumPaymentLimit",
          "INPUT",
        );
        const hasSinglePaymentLimit =
          singlePaymentLimit !== undefined &&
          singlePaymentLimit !== null &&
          singlePaymentLimit !== "";
        if (hasSinglePaymentLimit) {
          paymentTerm = `一次性付款不超過 ${Number(singlePaymentLimit).toLocaleString("en-US")} HKD`;
        } else {
          paymentTerm = "一次性付款";
        }
      } else {
        paymentTerm = paymentTermMap[paymentRange] || "";
      }
    }

    const benefitTermMap: Record<string, string> = {
      A: "少於1年",
      B: "1-5年",
      C: "6-10年",
      D: "11-15年",
      E: "16-20年",
      F: "超過20年",
      G: "終身",
    };
    const expectedTime = getQuestionValue(
      "financialAnalysis",
      "policyTargetExpectedTime",
      "RADIO",
    );
    const benefitTerm = expectedTime ? benefitTermMap[expectedTime] : undefined;

    return { objective, paymentTerm, benefitTerm };
  };

  const derived = getDerivedFields();
  const productsToShow =
    data.chooseProducts && data.chooseProducts.length > 0
      ? [
          ...(detailDisplay
            ? data.chooseProducts.filter((product) => !!product?.choose)
            : data.chooseProducts),
        ]
      : detailDisplay
        ? []
        : [{ ...newChooseProduct }];
  const selectedCount = productsToShow.filter((product) => !!product?.choose).length;

  return (
    <CustomCard
      title="產品介紹及選擇結果"
      className={`${styles.sectionCard} ${styles.zuuSectionCard}`}
    >
      <div ref={productSectionRef} className={styles.zuuProductToolbarBlock}>
        <div className={styles.zuuProductToolbar}>
          {!readOnly ? (
            <CustomButton
              bgColor="var(--theme-primary)"
              color="#fff"
              icon={<PlusOutlined />}
              onClick={() => {
                const nextSelection = [...(data.chooseProducts || [])];
                nextSelection.push({ ...newChooseProduct });
                applyChooseProducts(nextSelection);
              }}
            >
              新增產品
            </CustomButton>
          ) : null}
        </div>
      </div>

      <div className={`${styles.zuuSectionContent} ${styles.zuuProductSection}`}>
        <Row gutter={[16, 20]}>
          {productsToShow.map((product, index) => {
            const validation = productValidation[index] || {
              paymentTermValid: true,
              benefitTermValid: true,
            };

            const handleToggleSelect = (isChecking: boolean) => {
              if (readOnly) {
                return;
              }
              if (isChecking && selectedCount >= 6) {
                onMessageError("最多只能選擇6個產品");
                return;
              }

              const nextSelection = [...(data.chooseProducts || [])];
              if (!nextSelection[index]) {
                nextSelection[index] = { ...newChooseProduct };
              }
              nextSelection[index] = {
                ...nextSelection[index],
                choose: isChecking,
              };
              applyChooseProducts(nextSelection);
            };

            return (
              <Col span={24} key={index}>
                <CustomCard
                  onClick={(event) => {
                    const target = event.target as HTMLElement;
                    if (
                      target.closest(".ant-checkbox-wrapper") ||
                      target.closest(".ant-checkbox")
                    ) {
                      return;
                    }
                    handleToggleSelect(!product.choose);
                  }}
                  data-invalid={
                    !validation.paymentTermValid ||
                    !validation.benefitTermValid
                  }
                  title={
                    <div className={styles.zuuProductCardHeader}>
                      <Checkbox
                        checked={product.choose}
                        disabled={readOnly}
                        onChange={(event) => {
                          event.stopPropagation();
                          handleToggleSelect(event.target.checked);
                        }}
                      />
                      <span>介紹的產品{index + 1}</span>
                    </div>
                  }
                  extra={
                    product.choose || ((data.chooseProducts?.length || 0) > 1 && !readOnly && !detailDisplay) ? (
                      <div className={styles.zuuCardActions}>
                        {product.choose ? (
                          <CustomButton
                            bgColor="#ffffff"
                            color="#174c8f"
                            height={32}
                            fontSize={14}
                            className={styles.zuuSelectedBtn}
                            style={{
                              border: "2px solid #2d67b2",
                              borderRadius: 8,
                            }}
                          >
                            已選擇
                          </CustomButton>
                        ) : null}
                        {(data.chooseProducts?.length || 0) > 1 && !readOnly && !detailDisplay ? (
                          <CustomButton
                            bgColor="#ffffff"
                            color="#d4380d"
                            height={32}
                            fontSize={14}
                            className={`${styles.zuuSelectedBtn} ${styles.zuuOutlinedDangerBtn}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              const nextSelection = [...(data.chooseProducts || [])];
                              nextSelection.splice(index, 1);
                              applyChooseProducts(nextSelection);
                            }}
                            style={{
                              border: "2px solid #ff7875",
                              borderRadius: 8,
                            }}
                          >
                            刪除
                          </CustomButton>
                        ) : null}
                      </div>
                    ) : null
                  }
                  className={`${styles.zuuProductCard} ${
                    product.choose ? styles.zuuProductCardSelected : ""
                  }`}
                >
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item label="產品計劃">
                        {detailDisplay ? (
                          <DetailValue
                            content={product.productName || "-"}
                            className={styles.zuuReadonlyInputLike}
                          />
                        ) : readOnly ? (
                          <Input
                            readOnly
                            className={styles.zuuDerivedFieldInput}
                            value={product.productName || ""}
                            placeholder="請選擇"
                            suffix={<DownOutlined className={styles.zuuDerivedFieldIcon} />}
                          />
                        ) : (
                          <div onClick={(event) => event.stopPropagation()}>
                            <ProductSelectInput
                              value={product.productCode || ""}
                              initialName={product.productName}
                              context={{ type: "financial", index }}
                              onOpenSelector={onOpenSelector}
                              style={{ height: 40, borderRadius: 8 }}
                              disabled={readOnly}
                            />
                          </div>
                        )}
                      </Form.Item>
                    </Col>
                    {product.choose ? (
                      <>
                        <Col span={12}>
                          <Form.Item label="選購產品目標">
                            {detailDisplay ? (
                              <DetailValue
                                content={derived.objective}
                                className={styles.zuuReadonlyInputLike}
                              />
                            ) : (
                              <Input
                                readOnly
                                className={styles.zuuDerivedFieldInput}
                                value={derived.objective}
                                placeholder="請選擇"
                                suffix={<DownOutlined className={styles.zuuDerivedFieldIcon} />}
                              />
                            )}
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item label="選購產品的繳付年期">
                            {detailDisplay ? (
                              <DetailValue
                                content={derived.paymentTerm}
                                className={styles.zuuReadonlyInputLike}
                              />
                            ) : (
                              <Input
                                readOnly
                                className={styles.zuuDerivedFieldInput}
                                value={derived.paymentTerm}
                                placeholder="請選擇"
                                suffix={<DownOutlined className={styles.zuuDerivedFieldIcon} />}
                              />
                            )}
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item label="目標得益/保障年期">
                            {detailDisplay ? (
                              <DetailValue
                                content={derived.benefitTerm}
                                className={styles.zuuReadonlyInputLike}
                              />
                            ) : (
                              <Input
                                readOnly
                                className={styles.zuuDerivedFieldInput}
                                value={derived.benefitTerm}
                                placeholder="請選擇"
                                suffix={<DownOutlined className={styles.zuuDerivedFieldIcon} />}
                              />
                            )}
                          </Form.Item>
                        </Col>
                      </>
                    ) : null}
                  </Row>
                </CustomCard>
              </Col>
            );
          })}
        </Row>
      </div>
    </CustomCard>
  );
}

function ZuuOnlyProvideOneProductSection({
  data,
  onChange,
  detailDisplay = false,
}: ZuuOnlyProvideOneProductSectionProps & { detailDisplay?: boolean }) {
  const selectedProducts = (data.chooseProducts || []).filter(
    (product) => !!product?.choose,
  );
  const allSelectedHaveCompany = selectedProducts.every(
    (product) => !!product?.companyCode,
  );
  const uniqueCompanies = new Set(
    selectedProducts
      .map((product) => product.companyCode)
      .filter(Boolean),
  );
  const shouldShowReason =
    selectedProducts.length > 0 &&
    allSelectedHaveCompany &&
    uniqueCompanies.size === 1;

  if (!shouldShowReason) {
    return null;
  }

  return (
    <CustomCard
      title="是否只能提供一款保險產品"
      className={`${styles.sectionCard} ${styles.zuuSectionCard}`}
    >
      <div className={styles.zuuSectionContent}>
        {detailDisplay ? (
          <DetailSplitRow
            label="請在下方填寫只能提供一間保險公司建議之原因："
            value={
              <DetailValue
                content={data.onlyProvideOneProductReason}
                className={styles.zuuReadonlyInputLike}
              />
            }
          />
        ) : (
          <Form.Item
            label="請在下方填寫只能提供一間保險公司建議之原因："
            name="onlyProvideOneProductReason"
            rules={[{ required: true, message: "請輸入原因" }]}
          >
            <Input
              placeholder="請輸入"
              value={data.onlyProvideOneProductReason}
              onChange={(event) =>
                onChange({
                  ...data,
                  onlyProvideOneProductReason: event.target.value,
                })
              }
            />
          </Form.Item>
        )}
      </div>
    </CustomCard>
  );
}

function ZuuSuggestionsSection({
  data,
  onChange,
  getQuestionValue,
  detailDisplay = false,
}: ZuuSuggestionsSectionProps & { detailDisplay?: boolean }) {
  const applySuggestionValue = (
    questionKey: string,
    value: any,
    type: "RADIO" | "CHECKBOX" | "INPUT",
  ) => {
    const list = (data.suggestionsAndReasons || []).filter((q) => q.question);
    const newList = [...list];
    const questionIndex = newList.findIndex((q) => q.question === questionKey);

    let newItems: any[] = [];
    if (questionIndex >= 0) {
      newItems = [...(newList[questionIndex].items || [])];
    }

    if (type === "INPUT") {
      const itemIndex = newItems.findIndex((i) => i.key === "A");
      if (itemIndex >= 0) {
        newItems[itemIndex] = { ...newItems[itemIndex], value };
      } else {
        newItems.push({ key: "A", value });
      }
    } else if (type === "CHECKBOX") {
      const keys = (value as string[]) || [];
      newItems = newItems.map((i) => ({
        ...i,
        value: keys.includes(i.key),
      }));
      keys.forEach((k) => {
        if (!newItems.find((i) => i.key === k)) {
          newItems.push({ key: k, value: true });
        }
      });
    } else {
      const key = value;
      let keyFound = false;
      newItems = newItems.map((i) => {
        if (i.key === key) {
          keyFound = true;
          return { ...i, value: true };
        }
        return { ...i, value: false };
      });
      if (!keyFound && key) {
        newItems.push({ key, value: true });
      }
    }

    if (questionIndex >= 0) {
      newList[questionIndex] = {
        ...newList[questionIndex],
        question: questionKey,
        type:
          type === "CHECKBOX" ? "CHECKBOX" : type === "INPUT" ? "INPUT" : "RADIO",
        items: newItems,
      };
    } else {
      newList.push({
        question: questionKey,
        type:
          type === "CHECKBOX" ? "CHECKBOX" : type === "INPUT" ? "INPUT" : "RADIO",
        items: newItems,
      });
    }

    onChange({
      ...data,
      suggestionsAndReasons: newList,
    });
  };

  const recommendation =
    getQuestionValue(
      "suggestionsAndReasons",
      "reasonForTheRecommendation",
      "RADIO",
    ) || undefined;
  const reasonOptions =
    getQuestionValue(
      "suggestionsAndReasons",
      "reasonForTheRecommendation_B",
      "CHECKBOX",
    ) || [];

  const b1Options = reasonOptions.filter((item: string) => item.startsWith("B1_"));
  const b2Options = reasonOptions.filter((item: string) => item.startsWith("B2_"));
  const recommendationLabels: Record<string, string> = {
    A: "A. 此建議考慮到申請人的理財目標、全面保障的需要，其需要重要性及申請人的財務預算而作出，申請人期望以上各方面取得平衡。",
    B: "B. 產品推介與申請人財務需要分析選項不相符之原因。",
  };
  const b1Labels: Record<string, string> = {
    B1_A: "a. 申請人在進行財務需要分析時沒有表示有此選購產品的目標。",
    B1_B: "b. 最終選購產品的得益／保障年期與申請人的目標得益／保障年期／實現目標金額的預期時間不符。",
    B1_C: "c. 最終選購產品的人壽保障額與受保人需要的額外人壽保障額不符。",
    B1_D: "d. 最終選購產品的危疾保障額與受保人需要的額外危疾保障額不符。",
    B1_E: "e. 最終選購產品的目標儲蓄金額與申請人需要的額外目標儲蓄金額及／或額外投資回報金額不符。",
    B1_F: "f. 最終選購產品達至所述之投資回報金額的所需儲蓄／投資年期與申請人期望的年期不符。",
    B1_G: "g. 其他",
  };
  const b2Labels: Record<string, string> = {
    B2_A: "a. 我（中介人）已向申請人解釋其最終選購的保險產品並不適合其需要，但應申請人的喜好而作出介紹。",
    B2_B: "b. 我（中介人）已向申請人解釋其最終選購的保險產品並不適合其需要，但申請人經財務需要分析後要求介紹。",
    B2_C: "c. 我（中介人）已向申請人解釋其最終選購的保險產品並不適合其需要，但申請人因其親友購買了同一產品而要求介紹。",
    B2_D: "d. 縱使申請人最終選購的保險產品之保額／年期／回報與其財務需要分析所透露的資料不相符，我（中介人）認為此建議產品適合申請人。",
    B2_E: "e. 其他",
  };

  const updateReasonSubset = (prefix: "B1_" | "B2_", values: string[]) => {
    const otherValues = reasonOptions.filter((item: string) => !item.startsWith(prefix));
    applySuggestionValue(
      "reasonForTheRecommendation_B",
      [...otherValues, ...values],
      "CHECKBOX",
    );
  };

  return (
    <CustomCard
      title="建議及原因"
      className={`${styles.sectionCard} ${styles.zuuSectionCard}`}
    >
      <div className={styles.zuuSectionContent}>
        <div className={styles.zuuFormLabelText}>中介人建議原因（可選多於一項）</div>

        {detailDisplay ? (
          <DetailSplitRow
            label="中介人建議原因（可選多於一項）"
            value={
              <DetailValue
                content={recommendationLabels[recommendation || ""]}
                className={styles.zuuReadonlyInputLike}
              />
            }
          />
        ) : (
          <Form.Item
            className={styles.zuuCheckboxTopAlign}
            rules={[{ required: true, message: "請選擇一項" }]}
          >
            <Radio.Group
              className={styles.zuuOptionColumn}
              value={recommendation}
              onChange={(event) =>
                applySuggestionValue(
                  "reasonForTheRecommendation",
                  event.target.value,
                  "RADIO",
                )
              }
            >
              <Radio value="A" className={styles.zuuOptionLikeText}>
                A. 此建議考慮到申請人的理財目標、全面保障的需要，其需要重要性及申請人的財務預算而作出，申請人期望以上各方面取得平衡。
              </Radio>
              <Radio value="B" className={styles.zuuOptionLikeText}>
                B. 產品推介與申請人財務需要分析選項不相符之原因。
              </Radio>
            </Radio.Group>
          </Form.Item>
        )}

        {recommendation === "B" ? (
          <>
            <div className={styles.zuuInsetPanel}>
              <div className={styles.zuuFormLabelText}>B1. 產品推介與申請人財務需要分析選項不相符之原因</div>
              {detailDisplay ? (
                <div className={styles.zuuDetailStack}>
                  {b1Options.map((option: string) => (
                    <DetailSplitRow
                      key={option}
                      label={b1Labels[option] || option}
                      value={<DetailValue content="已選擇" className={styles.zuuReadonlyInputLike} />}
                    />
                  ))}
                </div>
              ) : (
                <Form.Item
                  className={styles.zuuCheckboxTopAlign}
                >
                  <Checkbox.Group
                    className={styles.zuuOptionColumn}
                    value={b1Options}
                    onChange={(values) =>
                      updateReasonSubset("B1_", values as string[])
                    }
                  >
                    <Checkbox value="B1_A">a. 申請人在進行財務需要分析時沒有表示有此選購產品的目標。</Checkbox>
                    <Checkbox value="B1_B">b. 最終選購產品的得益／保障年期與申請人的目標得益／保障年期／實現目標金額的預期時間不符。</Checkbox>
                    <Checkbox value="B1_C">c. 最終選購產品的人壽保障額與受保人需要的額外人壽保障額不符。</Checkbox>
                    <Checkbox value="B1_D">d. 最終選購產品的危疾保障額與受保人需要的額外危疾保障額不符。</Checkbox>
                    <Checkbox value="B1_E">e. 最終選購產品的目標儲蓄金額與申請人需要的額外目標儲蓄金額及／或額外投資回報金額不符。</Checkbox>
                    <Checkbox value="B1_F">f. 最終選購產品達至所述之投資回報金額的所需儲蓄／投資年期與申請人期望的年期不符。</Checkbox>
                    <Checkbox value="B1_G">g. 其他</Checkbox>
                  </Checkbox.Group>
                </Form.Item>
              )}
              {b1Options.includes("B1_G") ? (
                detailDisplay ? (
                  <DetailSplitRow
                    label="B1_G 補充"
                    value={
                      <DetailValue
                        content={getQuestionValue(
                          "suggestionsAndReasons",
                          "reasonForTheRecommendation_B_B1_G_remark",
                          "INPUT",
                        )}
                        className={styles.zuuReadonlyInputLike}
                      />
                    }
                  />
                ) : (
                  <Input
                    value={getQuestionValue(
                      "suggestionsAndReasons",
                      "reasonForTheRecommendation_B_B1_G_remark",
                      "INPUT",
                    )}
                    onChange={(event) =>
                      applySuggestionValue(
                        "reasonForTheRecommendation_B_B1_G_remark",
                        event.target.value,
                        "INPUT",
                      )
                    }
                    placeholder="請輸入"
                  />
                )
              ) : null}
            </div>

            <div className={styles.zuuInsetPanel}>
              <div className={styles.zuuFormLabelText}>B2. 建議原因（縱使適合性不符）</div>
              {detailDisplay ? (
                <div className={styles.zuuDetailStack}>
                  {b2Options.map((option: string) => (
                    <DetailSplitRow
                      key={option}
                      label={b2Labels[option] || option}
                      value={<DetailValue content="已選擇" className={styles.zuuReadonlyInputLike} />}
                    />
                  ))}
                </div>
              ) : (
                <Form.Item
                  className={styles.zuuCheckboxTopAlign}
                >
                  <Checkbox.Group
                    className={styles.zuuOptionColumn}
                    value={b2Options}
                    onChange={(values) =>
                      updateReasonSubset("B2_", values as string[])
                    }
                  >
                    <Checkbox value="B2_A">a. 我（中介人）已向申請人解釋其最終選購的保險產品並不適合其需要，但應申請人的喜好而作出介紹。</Checkbox>
                    <Checkbox value="B2_B">b. 我（中介人）已向申請人解釋其最終選購的保險產品並不適合其需要，但申請人經財務需要分析後要求介紹。</Checkbox>
                    <Checkbox value="B2_C">c. 我（中介人）已向申請人解釋其最終選購的保險產品並不適合其需要，但申請人因其親友購買了同一產品而要求介紹。</Checkbox>
                    <Checkbox value="B2_D">d. 縱使申請人最終選購的保險產品之保額／年期／回報與其財務需要分析所透露的資料不相符，我（中介人）認為此建議產品適合申請人。</Checkbox>
                    <Checkbox value="B2_E">e. 其他</Checkbox>
                  </Checkbox.Group>
                </Form.Item>
              )}
              {b2Options.includes("B2_E") ? (
                detailDisplay ? (
                  <DetailSplitRow
                    label="B2_E 補充"
                    value={
                      <DetailValue
                        content={getQuestionValue(
                          "suggestionsAndReasons",
                          "reasonForTheRecommendation_B_B2_E_remark",
                          "INPUT",
                        )}
                        className={styles.zuuReadonlyInputLike}
                      />
                    }
                  />
                ) : (
                  <Input
                    value={getQuestionValue(
                      "suggestionsAndReasons",
                      "reasonForTheRecommendation_B_B2_E_remark",
                      "INPUT",
                    )}
                    onChange={(event) =>
                      applySuggestionValue(
                        "reasonForTheRecommendation_B_B2_E_remark",
                        event.target.value,
                        "INPUT",
                      )
                    }
                    placeholder="請輸入"
                  />
                )
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </CustomCard>
  );
}
